/**
 * Helpers for SCORM iframe srcdoc rendering.
 *
 * WHY: Relative script/CSS paths in launch HTML (e.g. res/index.html → lms.js)
 * resolve against <base href>. Using the package root instead of the launch
 * file directory causes 404s and "iSpring is not defined" in iSpring packages.
 */

const SCORM_SRCDOC_CSS_RESET = `<style>
html, body {
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
  width: 100% !important;
  height: 100% !important;
}
/* iSpring and other div-based players mount into #content / #playerView */
#content, #playerView {
  width: 100% !important;
  height: 100% !important;
  min-height: 100% !important;
  overflow: visible !important;
}
/* Legacy SVG-overlay SCORM only — do not collapse iSpring body > div nodes */
body > svg { display: block; }
body:has(> svg) > div:not(svg ~ div) {
  height: 0 !important;
  overflow: hidden !important;
}
body:has(> svg) > svg ~ div {
  height: auto !important;
  overflow: visible !important;
}
</style>`;

/** Directory URL for the launch HTML file (trailing slash). */
export function getScormLaunchBaseHref(packageUrl: string, launchUrl: string): string {
    const normalizedPackage = packageUrl.endsWith('/') ? packageUrl : `${packageUrl}/`;
    const launchPath = launchUrl.replace(/^\//, '');
    const slashIdx = launchPath.lastIndexOf('/');
    const launchDir = slashIdx >= 0 ? launchPath.slice(0, slashIdx + 1) : '';
    return `${normalizedPackage}${launchDir}`;
}

/** Build fetch URL for the launch HTML document. */
export function getScormLaunchDocumentUrl(packageUrl: string, launchUrl: string): string {
    return `${packageUrl.replace(/\/$/, '')}/${launchUrl.replace(/^\//, '')}`;
}

/** Inject <base href> and layout reset into fetched SCORM HTML for srcdoc iframes. */
export function prepareScormSrcdoc(html: string, packageUrl: string, launchUrl: string): string {
    const baseTag = `<base href="${getScormLaunchBaseHref(packageUrl, launchUrl)}">`;

    if (/<head\b/i.test(html)) {
        return html.replace(/(<head\b[^>]*>)/i, `$1${baseTag}${SCORM_SRCDOC_CSS_RESET}`);
    }

    return `${baseTag}${SCORM_SRCDOC_CSS_RESET}${html}`;
}
